import api from './api';
import { mapResourceNode, mapResourceTopologyVersion, unwrapTopologyData } from './prodResourceTopologyMappers';
import type { CipLoop, CipLoopDraft, CipLoopWorkspace } from '../types/prodCipLoop';
import type { ResourceNode, ResourceTopologyVersion } from '../types/prodResourceTopology';

const ROOT = '/prod/resource-topologies';
type RawRecord = Record<string, unknown>;

const record = (value: unknown): RawRecord => value && typeof value === 'object' ? value as RawRecord : {};
const string = (value: unknown): string => value === undefined || value === null ? '' : String(value);
const nullable = (value: unknown): string | null => value === undefined || value === null ? null : String(value);

const mapLoop = (value: unknown): CipLoop => {
  const raw = record(value);
  const memberValues = raw.memberResourceIds ?? raw.member_resource_ids;
  const backupValues = raw.backupStationResourceIds ?? raw.backup_station_resource_ids;
  return {
    cipLoopId: string(raw.cipLoopId ?? raw.cip_loop_id),
    code: string(raw.code ?? raw.loopCode ?? raw.loop_code),
    name: string(raw.name ?? raw.loopName ?? raw.loop_name),
    lifecycleStatus: string(raw.lifecycleStatus ?? raw.lifecycle_status) as CipLoop['lifecycleStatus'],
    note: nullable(raw.note),
    memberResourceIds: Array.isArray(memberValues)
      ? memberValues.map(string)
      : [],
    primaryStationResourceId: nullable(raw.primaryStationResourceId ?? raw.primary_station_resource_id),
    backupStationResourceIds: Array.isArray(backupValues)
      ? backupValues.map((item) => {
        const backup = record(item);
        return { resourceId: string(backup.resourceId ?? backup.resource_id), priority: Number(backup.priority) };
      })
      : [],
    createdAt: string(raw.createdAt ?? raw.created_at),
    updatedAt: string(raw.updatedAt ?? raw.updated_at),
  };
};

const mapMutation = (value: unknown): { version: ResourceTopologyVersion; cipLoop: CipLoop } => {
  const raw = record(unwrapTopologyData(value));
  return { version: mapResourceTopologyVersion(raw.version), cipLoop: mapLoop(raw.cipLoop ?? raw.cip_loop) };
};

export const prodCipLoopApi = {
  async getWorkspace(versionId: string): Promise<CipLoopWorkspace> {
    const [versionResponse, resourcesResponse, loopsResponse] = await Promise.all([
      api.get(`${ROOT}/${versionId}`),
      api.get(`${ROOT}/${versionId}/nodes`),
      api.get(`${ROOT}/${versionId}/cip-loops`),
    ]);
    const resources = unwrapTopologyData(resourcesResponse.data);
    const loops = unwrapTopologyData(loopsResponse.data);
    return {
      version: mapResourceTopologyVersion(unwrapTopologyData(versionResponse.data)),
      resources: Array.isArray(resources) ? resources.map(mapResourceNode) : [] as ResourceNode[],
      loops: Array.isArray(loops) ? loops.map(mapLoop) : [],
    };
  },

  async create(versionId: string, expectedRevision: number, draft: CipLoopDraft) {
    const response = await api.post(`${ROOT}/${versionId}/cip-loops`, { revision: expectedRevision, ...draft });
    return mapMutation(response.data);
  },

  async update(versionId: string, cipLoopId: string, expectedRevision: number, draft: Partial<CipLoopDraft>) {
    const response = await api.patch(`${ROOT}/${versionId}/cip-loops/${cipLoopId}`, { revision: expectedRevision, ...draft });
    return mapMutation(response.data);
  },
};
